import type {
  PrescriptionEventDto,
  PrescriptionTrackDto,
  ReferralEventDto,
  ReferralTrackDto,
} from '../storage/WellCheckStorage';
import type { ActionDefDto } from '../storage/ActionDefStorage';
import type { AdminActionDto } from '../types/api/appProfile';

interface MedicalTrackSyncInput {
  actionsstate?: AdminActionDto[];
  prescriptiontrack?: PrescriptionTrackDto;
  referraltrack?: ReferralTrackDto;
  actionDef?: ActionDefDto | null;
  source?: string;
}

interface MedicalTrackSyncResult {
  actionsstate: AdminActionDto[];
  prescriptionEvents: PrescriptionEventDto[];
  referralEvents: ReferralEventDto[];
}

const RX_PREFIX = 'prescription_';
const REF_PREFIX = 'referral_';

function makeLocalId(prefix: string): string {
  const now = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}_${now}_${rand}`;
}

function normalisePrescriptionEvent(event: PrescriptionEventDto): PrescriptionEventDto {
  const trackValue = event.Track ?? event.track ?? '';
  const qtyValue = (event.qty ?? event.totalDoses ?? '').trim();
  const hasTrackText = trackValue.trim() !== '';
  const tracked = hasTrackText ? true : event.trackAction !== false;
  return {
    ...event,
    id: (event.id || '').trim() || makeLocalId('rx'),
    qty: qtyValue,
    totalDoses: qtyValue,
    Track: tracked ? (trackValue || 'Track') : '',
    trackAction: tracked,
  };
}

function normaliseReferralEvent(event: ReferralEventDto): ReferralEventDto {
  const trackValue = event.Track ?? event.track ?? '';
  const hasTrackText = trackValue.trim() !== '';
  const tracked = hasTrackText ? true : event.trackAction !== false;
  return {
    ...event,
    id: (event.id || '').trim() || makeLocalId('ref'),
    Track: tracked ? (trackValue || 'Track') : '',
    trackAction: tracked,
  };
}

function isFinishedStatus(value: string): boolean {
  const norm = (value || '').trim().toLowerCase();
  return norm === 'completed' || norm === 'cancelled';
}

function isMedicalAction(mvar: string): boolean {
  const key = (mvar || '').toLowerCase();
  return key.startsWith(RX_PREFIX) || key.startsWith(REF_PREFIX);
}

function getActionMVar(action?: AdminActionDto | null): string {
  return ((action as any)?.mvar ?? (action as any)?.MVar ?? '').toString();
}

function getActionSource(action?: AdminActionDto | null): string {
  return ((action as any)?.source ?? (action as any)?.Source ?? '').toString();
}

function getActionRank(action?: AdminActionDto | null): number | null {
  const raw = (action as any)?.rank ?? (action as any)?.Rank;
  if (raw === undefined || raw === null || raw === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function resolveActionId(actionDef: ActionDefDto | null | undefined, preferredName: string, fallbackId: string): string {
  if (!actionDef) return fallbackId;
  const preferred = preferredName.trim().toLowerCase();
  const byName = actionDef.actionTypes.find(t => (t.name || '').trim().toLowerCase() === preferred);
  if (byName?.action) return byName.action;
  const byId = actionDef.actionTypes.find(t => (t.action || '').trim() === fallbackId);
  if (byId?.action) return byId.action;
  return fallbackId;
}

export function syncMedicalTrackActions(input: MedicalTrackSyncInput): MedicalTrackSyncResult {
  const now = new Date().toISOString();
  const prescriptionEvents = (input.prescriptiontrack?.events ?? []).map(normalisePrescriptionEvent);
  const referralEvents = (input.referraltrack?.events ?? []).map(normaliseReferralEvent);
  const current = input.actionsstate ?? [];
  const source = input.source ?? '';
  const prescriptionActionId = resolveActionId(input.actionDef, 'Manage Prescription', '15');
  const referralActionId = resolveActionId(input.actionDef, 'Manage Referral', '11');

  const nonMedicalActions = current.filter(a => !isMedicalAction(getActionMVar(a)));
  const existingMedicalByMvar = new Map<string, AdminActionDto>();
  current
    .filter(a => isMedicalAction(getActionMVar(a)))
    .forEach(a => {
      const key = getActionMVar(a).toLowerCase();
      if (!existingMedicalByMvar.has(key)) {
        existingMedicalByMvar.set(key, a);
      }
    });

  const existingMaxRank = current.reduce((max, a) => {
    const rank = getActionRank(a);
    if (rank !== null) {
      return Math.max(max, rank);
    }
    return max;
  }, 0);
  let nextRank = existingMaxRank + 1;

  const medicalActions: AdminActionDto[] = [];

  prescriptionEvents.forEach((event) => {
    if (event.trackAction === false || !event.id || ((event.Track ?? event.track ?? '').trim() === '')) return;
    const mvar = `${RX_PREFIX}${event.id}`;
    const existing = existingMedicalByMvar.get(mvar.toLowerCase());
    const status = isFinishedStatus(event.status) ? '-1' : '1';
    const displaySource = (event.name || '').trim() || getActionSource(existing) || source;
    medicalActions.push({
      source: displaySource,
      group: '4',
      mvar,
      action: prescriptionActionId,
      dueDate: event.date || existing?.dueDate || '',
      location: existing?.location || '',
      schedule: '-1',
      actual: existing?.actual || '',
      expense: existing?.expense || '',
      importance: existing?.importance || '',
      outcome: existing?.outcome || '',
      created: existing?.created || now,
      completed: status === '-1' ? (existing?.completed || now) : '',
      status,
      rank: (getActionRank(existing) ?? nextRank++).toString(),
    });
  });

  referralEvents.forEach((event) => {
    if (event.trackAction === false || !event.id || ((event.Track ?? event.track ?? '').trim() === '')) return;
    const mvar = `${REF_PREFIX}${event.id}`;
    const existing = existingMedicalByMvar.get(mvar.toLowerCase());
    const status = isFinishedStatus(event.status) ? '-1' : '1';
    const displaySource = (event.to || '').trim() || getActionSource(existing) || source;
    medicalActions.push({
      source: displaySource,
      group: '4',
      mvar,
      action: referralActionId,
      dueDate: event.date || existing?.dueDate || '',
      location: event.contactInfo || existing?.location || '',
      schedule: '-1',
      actual: existing?.actual || '',
      expense: existing?.expense || '',
      importance: existing?.importance || '',
      outcome: event.outcome || existing?.outcome || '',
      created: existing?.created || now,
      completed: status === '-1' ? (existing?.completed || now) : '',
      status,
      rank: (getActionRank(existing) ?? nextRank++).toString(),
    });
  });

  return {
    actionsstate: [...nonMedicalActions, ...medicalActions],
    prescriptionEvents,
    referralEvents,
  };
}
